import { useEffect, useMemo, useState, useRef } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import '../styles/JobApplications.css';
import { JobApplication } from '../types/jobs';

import Modal from '../components/common/Modal';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

import TableWithSearchCTA from '../components/TableWithSearchCTA';
import AgentOnboard from '../agent/AgentOnboard';
import { useJobApplicationColumns } from '../components/columns/useJobApplicationCol';
import { fetchJobListings } from '../hooks/queries';
import FilterSidebar, {
  SidebarFilters,
} from '../components/common/FilterSidebar';
import {
  languageOptions,
  jobApplicationStatusOptions,
} from '../constants';

const initialFilters: SidebarFilters = {
  industry: [],
  language: [],
  applicationStatus: [],
  screeningStatus: [],
};

const JobApplications: React.FC = () => {
  const { data: jobListings } = fetchJobListings();
  const gridRef = useRef<any>(null);
  const [params] = useSearchParams();
  const email = params.get('email');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] =
    useState<JobApplication | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sidebarFilters, setSidebarFilters] =
    useState<SidebarFilters>(initialFilters);
  const [distinctIndustries, setDistinctIndustries] = useState<string[]>([]);
  const [filterCounts, setFilterCounts] = useState<Record<string, Record<string, number>>>({});
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  // Group industries by first word (e.g. "Ecommerce & finance" + "Ecommerce and spend" → "Ecommerce")
  const industryGroups = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const ind of distinctIndustries) {
      const firstWord = ind.split(/[\s&,]+/)[0];
      const key = firstWord.charAt(0).toUpperCase() + firstWord.slice(1).toLowerCase();
      if (!groups[key]) groups[key] = [];
      groups[key].push(ind);
    }
    return groups;
  }, [distinctIndustries]);

  // Keep a ref so expandedSidebarFilters doesn't re-trigger the dataSource
  // when distinctIndustries updates from API responses (avoids infinite loop)
  const industryGroupsRef = useRef<Record<string, string[]>>({});
  useEffect(() => {
    industryGroupsRef.current = industryGroups;
  }, [industryGroups]);

  // Expand grouped industry keys to original values before sending to API
  // Only depends on sidebarFilters — uses ref for industryGroups to break the cycle
  const expandedSidebarFilters = useMemo<SidebarFilters>(() => ({
    ...sidebarFilters,
    industry: sidebarFilters.industry.flatMap(
      (key) => industryGroupsRef.current[key] || [key]
    ),
  }), [sidebarFilters]);

  // Set first category as active when job listings are loaded
  useEffect(() => {
    if (jobListings?.data && !selectedCategory) {
      const firstCategory = jobListings.data.find(
        (job) => job.category
      )?.category;
      if (firstCategory) {
        setSelectedCategory(firstCategory);
      }
    }
  }, [jobListings?.data, selectedCategory]);

  const navigate = useNavigate();

  const columnDefs = useJobApplicationColumns({
    onView: (data) => navigate(`/jobs/${data._id}`),
    onBoard: (data) => {
      setSelectedApplication(data);
      setIsModalOpen(true);
    },
  });

  const onModalClose = () => {
    setIsModalOpen(false);
    setSelectedApplication(null);
  };

  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
  };

  // Clicking a status chip toggles it in sidebarFilters (synced with sidebar checkboxes)
  const handleStatusToggle = (status: string) => {
    if (status === '') {
      // "All" clicked - clear all status filters, this is done in order to sync with sidebar
      setSidebarFilters((prev) => ({ ...prev, applicationStatus: [] }));
      return;
    }
    setSidebarFilters((prev) => {
      const current = prev.applicationStatus;
      const updated = current.includes(status)
        ? current.filter((s) => s !== status)
        : [...current, status];
      return { ...prev, applicationStatus: updated };
    });
  };

  const uniqueCategories = () => {
    const categories =
      jobListings?.data
        ?.map((job: any) => job.category)
        .filter((category) => category !== null && category !== undefined) ||
      [];

    return [...new Set(categories)];
  };

  const handleRefresh = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.purgeInfiniteCache();
      gridRef.current.api.paginationGoToFirstPage();
    }
  };

  const filterSections = useMemo(() => {
    const industryCounts = filterCounts.industry || {};
    const languageCounts = filterCounts.language || {};
    const appStatusCounts = filterCounts.applicationStatus || {};
    const screeningCounts = filterCounts.screeningStatus || {};

    return [
      {
        key: 'industry' as const,
        label: 'Industry',
        searchable: true,
        options: Object.entries(industryGroups).map(([key, values]) => ({
          value: key,
          label: key,
          count: values.reduce((sum, v) => sum + (industryCounts[v] || 0), 0),
        })),
      },
      {
        key: 'language' as const,
        label: 'Language',
        options: languageOptions.map((lang) => ({
          value: lang,
          label: lang,
          count: languageCounts[lang] ?? 0,
        })),
      },
      {
        key: 'applicationStatus' as const,
        label: 'Application Status',
        options: jobApplicationStatusOptions
          .filter((opt) => opt.value !== '')
          .map((opt) => ({
            value: opt.value,
            label: opt.label,
            count: appStatusCounts[opt.value] ?? 0,
          })),
      },
      {
        key: 'screeningStatus' as const,
        label: 'Assessment Result',
        options: [
          { value: 'pass', label: 'Pass', count: screeningCounts['pass'] ?? 0 },
          { value: 'fail', label: 'Fail', count: screeningCounts['fail'] ?? 0 },
        ],
      },
    ];
  }, [industryGroups, filterCounts]);

  return (
    <div className="w-full flex h-full">
      <div className="flex-1 flex flex-col min-w-0">
        <TableWithSearchCTA
          columnDefs={columnDefs as any}
          ctas={{
            enableExportToCsv: true,
          }}
          headerTitle="Job Applications"
          description="View and manage job applications"
          enableRefresh
          storageKey="job_applications_table"
          showCategoryFilter={true}
          selectedCategory={selectedCategory}
          uniqueCategories={uniqueCategories() as string[]}
          onCategorySelect={handleCategorySelect}
          showStatusFilter={true}
          selectedStatus={sidebarFilters.applicationStatus}
          onStatusSelect={handleStatusToggle}
          refetch={handleRefresh}
          tableRef={gridRef}
          alwaysShowHorizontalScroll={true}
          rowModelType="infinite"
          cacheBlockSize={50}
          mode="server"
          initialSearch={email || ''}
          sidebarFilters={expandedSidebarFilters}
          onDistinctIndustries={setDistinctIndustries}
          onFilterCounts={setFilterCounts}
          isFilterOpen={isFilterOpen}
          onToggleFilter={() => setIsFilterOpen((prev) => !prev)}
          activeFilterCount={Object.values(sidebarFilters).reduce((sum, arr) => sum + arr.length, 0)}
        />
      </div>
      <FilterSidebar
        filters={sidebarFilters}
        onChange={setSidebarFilters}
        sections={filterSections}
        isOpen={isFilterOpen}
        onToggle={setIsFilterOpen}
      />
      <Modal
        isOpen={isModalOpen}
        onRequestClose={onModalClose}
        title="Send Onboarding Email"
        width="25%"
        height="fit-content"
      >
        {selectedApplication && (
          <AgentOnboard
            initialData={selectedApplication}
            onSuccess={() => onModalClose()}
            onError={(e) => toast.error(e.message)}
          />
        )}
      </Modal>
    </div>
  );
};

export default JobApplications;
